import gql from 'graphql-tag';
import { useState } from 'react';
import { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@watershed/ui-core/components/Tooltip';
import Typography from '@mui/material/Typography';

import { Formik } from 'formik';
import { useRouter } from 'next/router';

import AttachmentIcon from '@watershed/icons/components/Attachment';
import CloseIcon from '@watershed/icons/components/Close';
import DocumentIcon from '@watershed/icons/components/Document';
import ErrorIcon from '@watershed/icons/components/Error';
import Callout from '@watershed/shared-frontend/components/Callout';
import { routeForSupportCase } from '@watershed/shared-universal/dashboardRoutes';
import {
  getIssueTypeLabel,
  getPriorityDescription,
  getPriorityLabel,
} from '@watershed/shared-universal/appPlat/supportUtils';
import {
  MAX_SUPPORT_CASE_DESCRIPTION_LENGTH,
  MAX_SUPPORT_CASE_SUBJECT_LENGTH,
} from '@watershed/shared-universal/support/constants';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import Button from '@watershed/ui-core/components/Button';
import DialogForm from '@watershed/ui-core/components/DialogForm';
import { UploadFile } from '@watershed/ui-core/components/FileUpload/FileUploadDropzone';
import Field from '@watershed/ui-core/components/Form/Field';
import SelectField from '@watershed/ui-core/components/Form/SelectField';
import TextField from '@watershed/ui-core/components/Form/TextField';
import IconButton from '@watershed/ui-core/components/IconButton';
import Well from '@watershed/ui-core/components/Well';
import { createDialogHook } from '@watershed/ui-core/hooks/useDialog';

import {
  GQCreateSupportCaseInput,
  GQSupportCaseIssueType,
  GQSupportCasePriority,
} from '../../generated/graphql-schema-types';

import {
  MarkdownTextFieldWithPreviewForMentions,
  MentionedUser,
} from '../Mentions';
import SupportCaseAttachmentDialog from './SupportCaseAttachmentDialog';
import { SupportCaseFAQCollapsible } from './SupportCaseFAQ';
import SupportCasePriorityIcon from './SupportCasePriorityIcon';
import useManageSupportCaseAttachments from './useManageSupportCaseAttachments';

gql`
  mutation CreateSupportCase(
    $input: CreateSupportCaseInput!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) @withOwner(owner: ApplicationPlatform) {
    createSupportCase(input: $input) {
      supportCase {
        ...SupportCaseForSupportCasePage
      }
    }
  }
`;

// We support old issue types for backwards compatibility,
// but we only want these to be available for new support cases.
const AVAILABLE_ISSUE_TYPES = [
  GQSupportCaseIssueType.GeneralPlatform,
  GQSupportCaseIssueType.DataUpload,
  GQSupportCaseIssueType.DatasetGuidance,
  GQSupportCaseIssueType.Measurement,
  GQSupportCaseIssueType.Methodology,
  GQSupportCaseIssueType.SupplyChain,
  GQSupportCaseIssueType.Footprint,
  GQSupportCaseIssueType.Finance,
  GQSupportCaseIssueType.Reporting,
] as const;
export type AvailableIssueType = (typeof AVAILABLE_ISSUE_TYPES)[number];

const ISSUE_TYPE_TO_CALLOUT_TEXT: Partial<
  Record<GQSupportCaseIssueType, MessageDescriptor>
> = {
  [GQSupportCaseIssueType.Measurement]: msg({
    message:
      'For cases about measurements, please include the measurement project name, the task name, the file name, and any available links or screenshots.',
    context: 'Callout text for measurement issue type',
  }),
  [GQSupportCaseIssueType.DataUpload]: msg({
    message:
      'For cases about data uploads, please include the measurement project name, the task name, the file name, and any available links or screenshots.',
    context: 'Callout text for data upload issue type',
  }),
  [GQSupportCaseIssueType.Footprint]: msg({
    message:
      'For cases about footprints, please include the footprint name, the footprint version, the measurement project name, and any available links or screenshots.',
    context: 'Callout text for footprint issue type',
  }),
};

export type SupportCaseDialogProps = {
  onClose: () => void;
  issueType: AvailableIssueType;
  isSupplierSupport?: boolean;
  objectId?: string | null;
};

export default function NewSupportCaseDialog({
  onClose,
  objectId,
  issueType,
  isSupplierSupport,
}: SupportCaseDialogProps) {
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showAttachmentDialog, setShowAttachmentDialog] = useState(false);
  const [files, setFiles] = useState<Array<UploadFile>>([]);
  const {
    executeSubmit,
    isSubmitting,
    removeUpload,
    removeAllUploads,
    getDownloadFileUrl,
  } = useManageSupportCaseAttachments({ files, setFiles });
  const router = useRouter();
  const theme = useTheme();
  const { t, i18n } = useLingui();
  const [selectedPriority, setSelectedPriority] =
    useState<GQSupportCasePriority | null>(null);
  const [mentionedUsers, setMentionedUsers] = useState<Array<MentionedUser>>(
    []
  );

  const getCalloutComponent = (
    issueType: GQSupportCaseIssueType
  ): React.ReactNode => {
    const calloutText = ISSUE_TYPE_TO_CALLOUT_TEXT[issueType];
    if (calloutText) {
      return <Callout description={i18n._(calloutText)} />;
    }
    return null;
  };

  return (
    <>
      <Formik<GQCreateSupportCaseInput>
        initialValues={{
          subject: '',
          description: '',
          priority: GQSupportCasePriority.Low,
          issueType,
          objectId,
          mentionedUserIds: [],
        }}
        onSubmit={async (input) => {
          if (errorMsg) {
            setErrorMsg(null);
          }
          const result = await executeSubmit({
            input: {
              ...input,
              mentionedUserIds: mentionedUsers.map((user) => user.id),
              attachments: files.map((file) => {
                return {
                  fileMetadataId: file.fileId,
                  fileName: file.filename,
                };
              }),
            },
            after: null,
            before: null,
            first: 100,
            last: null,
          });
          if (result.data?.createSupportCase?.supportCase) {
            void router.push(
              routeForSupportCase(
                result.data.createSupportCase.supportCase.id,
                {
                  conf: true,
                  isSupplierSupport,
                }
              )
            );
          } else {
            if (result.error) {
              setErrorMsg(
                'There was an error submitting your support case. Watershed has been notified.'
              );
            }
          }
          onClose();
        }}
      >
        {(form) => (
          <DialogForm
            onClose={() => {
              onClose();
              // Give yourself a breath for the animation
              setTimeout(() => {
                form.resetForm();
                setErrorMsg(null);
              }, 300);
            }}
            maxWidth="lg"
            header={{
              title: (
                <Trans context="form header copy for creating support cases">
                  Help
                </Trans>
              ),
            }}
            sx={{
              // Dialogs play me close like butter plays toast
              '& .MuiDialogContent-root': { rowGap: 2, paddingBlock: 0 },
            }}
            isSubmitting={isSubmitting}
            submit={(() => {
              const subjectOverLimit =
                form.values.subject.length > MAX_SUPPORT_CASE_SUBJECT_LENGTH;
              const descriptionOverLimit =
                (form.values.description?.length ?? 0) >
                MAX_SUPPORT_CASE_DESCRIPTION_LENGTH;
              const isOverLimit = subjectOverLimit || descriptionOverLimit;
              const subjectEmpty =
                !form.values.subject || form.values.subject === '';
              const descriptionEmpty =
                !form.values.description || form.values.description === '';

              const getTooltipMessage = () => {
                if (subjectOverLimit && descriptionOverLimit) {
                  return t({
                    message: 'Subject and description exceed character limits',
                    context: 'Tooltip for disabled submit button',
                  });
                }
                if (subjectOverLimit) {
                  return t({
                    message: 'Subject exceeds character limit',
                    context: 'Tooltip for disabled submit button',
                  });
                }
                if (descriptionOverLimit) {
                  return t({
                    message: 'Description exceeds character limit',
                    context: 'Tooltip for disabled submit button',
                  });
                }
                if (subjectEmpty && descriptionEmpty) {
                  return t({
                    message: 'Subject and description are required',
                    context: 'Tooltip for disabled submit button',
                  });
                }
                if (subjectEmpty) {
                  return t({
                    message: 'Subject is required',
                    context: 'Tooltip for disabled submit button',
                  });
                }
                if (descriptionEmpty) {
                  return t({
                    message: 'Description is required',
                    context: 'Tooltip for disabled submit button',
                  });
                }
                return '';
              };

              const isDisabled =
                isSubmitting ||
                subjectEmpty ||
                descriptionEmpty ||
                !form.values.issueType ||
                isOverLimit;

              const showTooltip =
                isOverLimit || subjectEmpty || descriptionEmpty;

              return (
                <Tooltip
                  title={showTooltip ? getTooltipMessage() : ''}
                  slotProps={{
                    tooltip: {
                      sx: {
                        textAlign: 'center',
                      },
                    },
                  }}
                >
                  <span>
                    <Button
                      color="primary"
                      type="submit"
                      disabled={isDisabled}
                      data-testid={TestIds.SupportCaseSubmitButton}
                    >
                      <Trans context="Button copy to submit a support case">
                        Submit
                      </Trans>
                    </Button>
                  </span>
                </Tooltip>
              );
            })()}
          >
            <Stack
              direction="row"
              gap={2}
              divider={<Divider flexItem orientation="vertical" />}
              sx={{ display: 'grid', gridTemplateColumns: '3fr 1px 2fr' }}
            >
              <Stack sx={{ paddingBlock: 2 }} gap={1}>
                <Stack
                  sx={{
                    borderBottom: `1px dashed ${theme.palette.divider}`,
                    pb: 2,
                  }}
                >
                  <Typography variant="h4">
                    <Trans context="Section header for a support case submission form">
                      What seems to be the issue?
                    </Trans>
                  </Typography>
                  <Typography variant="body2" sx={{ textWrap: 'balance' }}>
                    <Trans>
                      If you’ve found a bug or something isn’t working as
                      expected, please tell us the steps you took so we can
                      reproduce the issue and help fix it.
                    </Trans>
                  </Typography>
                </Stack>

                {errorMsg && (
                  <Callout
                    title={
                      <Trans>
                        There was an error submitting your support case.
                      </Trans>
                    }
                    variant="error"
                    IconComponent={ErrorIcon}
                    onDismiss={() => setErrorMsg(null)}
                    description={errorMsg}
                  />
                )}
                <Stack gap={0.5} sx={{ width: '100%' }}>
                  <TextField
                    id="subject"
                    required
                    autoFocus
                    label={
                      <Trans context="Form label for support case subject">
                        Subject
                      </Trans>
                    }
                    data-testid={TestIds.SupportCaseSubjectInput}
                  />
                  <Typography
                    sx={{
                      alignSelf: 'flex-end',
                      color: (theme) => {
                        const numChars = form.values.subject.length;
                        if (numChars > MAX_SUPPORT_CASE_SUBJECT_LENGTH) {
                          return theme.palette.error.main;
                        }
                        return theme.palette.text.secondary;
                      },
                    }}
                    variant="caption"
                  >
                    {form.values.subject.length}/
                    {MAX_SUPPORT_CASE_SUBJECT_LENGTH}
                  </Typography>
                </Stack>
                <SelectField
                  id="issueType"
                  label={
                    <Trans context="Form label for support case feature area">
                      Product area
                    </Trans>
                  }
                  data-testid={TestIds.SupportCaseProductAreaSelect}
                >
                  {AVAILABLE_ISSUE_TYPES.map((value) => (
                    <MenuItem key={value} value={value}>
                      {getIssueTypeLabel(value)}
                    </MenuItem>
                  ))}
                </SelectField>
                <SelectField
                  id="priority"
                  label={
                    <Trans context="Form label for support case priority">
                      Priority
                    </Trans>
                  }
                  data-testid={TestIds.SupportCasePrioritySelect}
                  renderValue={(value) =>
                    getPriorityLabel(value as GQSupportCasePriority)
                  }
                >
                  {[
                    GQSupportCasePriority.Low,
                    GQSupportCasePriority.Medium,
                    GQSupportCasePriority.High,
                  ].map((priority) => (
                    <MenuItem
                      key={priority}
                      value={priority}
                      sx={{
                        height: 40,
                        gap: 1,
                        '&& .MuiListItemIcon-root': {
                          minWidth: 'fit-content',
                          pr: 1,
                        },
                      }}
                      onMouseEnter={() => setSelectedPriority(priority)}
                      onMouseLeave={() => setSelectedPriority(null)}
                      onFocus={() => setSelectedPriority(priority)}
                      onBlur={() => setSelectedPriority(null)}
                    >
                      <ListItemIcon>
                        <SupportCasePriorityIcon priority={priority} />
                      </ListItemIcon>
                      <ListItemText>{getPriorityLabel(priority)}</ListItemText>
                    </MenuItem>
                  ))}
                  <Stack
                    sx={{
                      height: '44px',
                      position: 'relative',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey05',
                      borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                    role="status" // Indicates this is a status that may update
                    aria-live="polite" // Announces changes politely
                    aria-atomic="true" // Reads the entire content when it changes
                  >
                    {[
                      { priority: null, text: getPriorityDescription(null) },
                      {
                        priority: GQSupportCasePriority.High,
                        text: getPriorityDescription(
                          GQSupportCasePriority.High
                        ),
                      },
                      {
                        priority: GQSupportCasePriority.Medium,
                        text: getPriorityDescription(
                          GQSupportCasePriority.Medium
                        ),
                      },
                      {
                        priority: GQSupportCasePriority.Low,
                        text: getPriorityDescription(GQSupportCasePriority.Low),
                      },
                    ].map(({ priority, text }) => (
                      <Typography
                        key={priority ?? 'default'}
                        sx={{
                          fontFamily: (theme) =>
                            theme.typography.fontFamilyMonospace,
                          fontSize: 12,
                          color: 'text.secondary',
                          textAlign: 'center',
                          transition: 'opacity 0.15s ease-out',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                        }}
                        style={{
                          opacity: selectedPriority === priority ? 1 : 0,
                          // Hide from screen readers when not visible
                          visibility:
                            selectedPriority === priority
                              ? 'visible'
                              : 'hidden',
                          // Prevent interaction when not visible
                          pointerEvents:
                            selectedPriority === priority ? 'auto' : 'none',
                        }}
                        variant="body3"
                        aria-hidden={selectedPriority !== priority} // Hide from screen readers when not visible
                      >
                        {text}
                      </Typography>
                    ))}
                    {/* Add a visually hidden element that announces the current selection */}
                    <Box
                      component="span"
                      sx={{ display: 'none' }}
                      role="status"
                    >
                      {selectedPriority
                        ? `Selected priority: ${selectedPriority}. ${getPriorityDescription(
                            selectedPriority
                          )}`
                        : getPriorityDescription(null)}
                    </Box>
                  </Stack>
                </SelectField>
                {form.values.issueType &&
                  getCalloutComponent(form.values.issueType)}
                <Field
                  inputId="description"
                  required
                  label={
                    <Trans context="Form label for support case description">
                      Description
                    </Trans>
                  }
                >
                  <Stack gap={0.5} sx={{ width: '100%' }}>
                    <MarkdownTextFieldWithPreviewForMentions
                      id="description"
                      name="description"
                      minRows={3}
                      maxRows={10}
                      message={form.values.description ?? ''}
                      setMessageText={(value: string) =>
                        form.setFieldValue('description', value)
                      }
                      mentionedUsers={mentionedUsers}
                      setMentionedUsers={setMentionedUsers}
                      placeholder={t({
                        message:
                          'Add more details here… Use @ to mention users',
                        context: 'Placeholder for support case description',
                      })}
                      sx={{ '& textarea': { resize: 'none' } }}
                      data-testid={TestIds.SupportCaseDescriptionInput}
                    />
                    <Typography
                      sx={{
                        alignSelf: 'flex-end',
                        color: (theme) => {
                          const numChars = form.values.description?.length ?? 0;
                          if (numChars > MAX_SUPPORT_CASE_DESCRIPTION_LENGTH) {
                            return theme.palette.error.main;
                          }
                          return theme.palette.text.secondary;
                        },
                      }}
                      variant="caption"
                    >
                      {form.values.description?.length ?? 0}/
                      {MAX_SUPPORT_CASE_DESCRIPTION_LENGTH}
                    </Typography>
                  </Stack>
                </Field>
                <Stack direction="column" gap={0.5}>
                  <div>
                    <Button
                      color="secondary"
                      onClick={() => setShowAttachmentDialog(true)}
                      data-testid={TestIds.SupportAttachmentButton}
                      startIcon={<AttachmentIcon />}
                      sx={{ width: '100%' }}
                    >
                      <Trans context="Button copy to include an attached file in a support case creation dialog">
                        Include attachment
                      </Trans>
                    </Button>
                  </div>
                  {files.map((file) => (
                    <Well key={file.fileId}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            alignItems: 'center',
                          }}
                        >
                          <DocumentIcon
                            size={14}
                            color={(theme) => theme.palette.grey50}
                            sx={{ flexShrink: 0 }}
                          />
                          <Typography>{file.filename}</Typography>
                        </Box>
                        <IconButton
                          onClick={async () => {
                            await removeUpload(file.fileId);
                          }}
                        >
                          <CloseIcon color={(theme) => theme.palette.grey50} />
                        </IconButton>
                      </Stack>
                    </Well>
                  ))}
                </Stack>
              </Stack>
              <Stack sx={{ paddingBlock: 2 }}>
                <SupportCaseFAQCollapsible />
              </Stack>
            </Stack>
          </DialogForm>
        )}
      </Formik>
      <SupportCaseAttachmentDialog
        open={showAttachmentDialog}
        onClose={async () => {
          setShowAttachmentDialog(false);
          await removeAllUploads();
        }}
        onSubmit={(files: Array<UploadFile>) => {
          setFiles(files);
          setShowAttachmentDialog(false);
        }}
        removeUpload={removeUpload}
        files={files}
        getDownloadFileUrl={getDownloadFileUrl}
      />
    </>
  );
}

export const useSupportCaseDialog =
  createDialogHook<SupportCaseDialogProps>(NewSupportCaseDialog);
